// src/features/auth/components/RegisterForm.tsx
import React, { useState, useEffect } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import authService from '../services/authService';
import { RegisterRequest } from '../types';
import PasswordTooltip from './PasswordTooltip';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';
import PasswordRequirements from './PasswordRequirements';

interface RegisterFormProps {
  onSwitchToLogin: () => void;
  onRegisterSuccess?: (response: any) => void; // Optional: callback for successful registration
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSwitchToLogin, onRegisterSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [organizationCode, setOrganizationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Client-side password validation with sequential character detection
  const isPasswordValid = (pwd: string): boolean => {
    // Helper function to detect sequential characters
    const hasSequentialChars = (password: string): boolean => {
      // Check for 3+ consecutive ascending characters (123, abc, ABC)
      for (let i = 0; i < password.length - 2; i++) {
        const char1 = password.charCodeAt(i);
        const char2 = password.charCodeAt(i + 1);
        const char3 = password.charCodeAt(i + 2);
        
        if (char2 === char1 + 1 && char3 === char2 + 1) {
          return false; // Invalid if sequential found
        }
      }
      
      // Check for 3+ consecutive descending characters (321, cba, CBA)
      for (let i = 0; i < password.length - 2; i++) {
        const char1 = password.charCodeAt(i);
        const char2 = password.charCodeAt(i + 1);
        const char3 = password.charCodeAt(i + 2);
        
        if (char2 === char1 - 1 && char3 === char2 - 1) {
          return false; // Invalid if sequential found
        }
      }
      
      return true; // No sequential characters found
    };

    const criteria = [
      pwd.length >= 8,
      /[A-Z]/.test(pwd),
      /[a-z]/.test(pwd),
      /\d/.test(pwd),
      /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd),
      hasSequentialChars(pwd) // No sequential characters
    ];
    
    // All 6 criteria must be met
    return criteria.every(Boolean);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setPasswordErrors([]);
    setLoading(true);

    // Validation checks
    if (!email || !password || !firstName || !lastName /* || !group */) {
      setError('All fields are required.');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    if (!isPasswordValid(password)) {
      setError('Password does not meet security requirements. Please check the password requirements below.');
      setLoading(false);
      return;
    }

    const registerData: RegisterRequest = { 
      email, 
      password, 
      first_name: firstName, 
      last_name: lastName,
      organization_code: organizationCode,
    };

    try {
      const response = await authService.register(registerData);
      
      if (onRegisterSuccess) {
        onRegisterSuccess(response);
      }
      // Optionally, log the user in directly or switch to login view
      // onSwitchToLogin(); 
    } catch (err: any) {
      // Handle password-specific validation errors
      if (err?.field_errors?.password) {
        setPasswordErrors(err.field_errors.password);
        setError('Password does not meet requirements. Please see details below.');
      } else if (err?.details?.password) {
        setPasswordErrors(Array.isArray(err.details.password) ? err.details.password : [err.details.password]);
        setError('Password does not meet requirements. Please see details below.');
      } else {
        setError(err.error || err.message || 'Failed to register. Please try again.');
      }
    }
    setLoading(false);
  };

  return (
    <div className="p-6 sm:p-8 bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary rounded-lg shadow-xl w-full max-w-md">
      <h2 className="text-2xl font-semibold text-center text-theme-text dark:text-dark-theme-text mb-6">
        Create Your Account
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-1">First Name</label>
          <input id="firstName" name="firstName" type="text" autoComplete="given-name" required value={firstName} onChange={(e) => setFirstName(e.target.value)}
            className="appearance-none block w-full px-3 py-2 border border-theme-border dark:border-dark-theme-border rounded-md shadow-sm placeholder-theme-text-tertiary dark:placeholder-dark-theme-text-tertiary focus:outline-none focus:ring-primary-light dark:focus:ring-primary-dark focus:border-primary-light dark:focus:border-primary-dark sm:text-sm bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text"
            placeholder="Your First Name"
          />
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-1">Last Name</label>
          <input id="lastName" name="lastName" type="text" autoComplete="family-name" required value={lastName} onChange={(e) => setLastName(e.target.value)}
            className="appearance-none block w-full px-3 py-2 border border-theme-border dark:border-dark-theme-border rounded-md shadow-sm placeholder-theme-text-tertiary dark:placeholder-dark-theme-text-tertiary focus:outline-none focus:ring-primary-light dark:focus:ring-primary-dark focus:border-primary-light dark:focus:border-primary-dark sm:text-sm bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text"
            placeholder="Your Last Name"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-1">Email Address</label>
          <input id="email" name="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="appearance-none block w-full px-3 py-2 border border-theme-border dark:border-dark-theme-border rounded-md shadow-sm placeholder-theme-text-tertiary dark:placeholder-dark-theme-text-tertiary focus:outline-none focus:ring-primary-light dark:focus:ring-primary-dark focus:border-primary-light dark:focus:border-primary-dark sm:text-sm bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text"
            placeholder="you@example.com"
          />
        </div>
        
        <div>
          <label htmlFor="organizationCode" className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-1">
            Organization Code
          </label>
          <input 
            id="organizationCode" 
            name="organizationCode" 
            type="text" 
            required 
            value={organizationCode} 
            onChange={(e) => setOrganizationCode(e.target.value.toUpperCase())}
            className="appearance-none block w-full px-3 py-2 border border-theme-border dark:border-dark-theme-border rounded-md shadow-sm placeholder-theme-text-tertiary dark:placeholder-dark-theme-text-tertiary focus:outline-none focus:ring-primary-light dark:focus:ring-primary-dark focus:border-primary-light dark:focus:border-primary-dark sm:text-sm bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text font-mono"
            placeholder="Enter 8-character code"
            maxLength={8}
          />
          <p className="mt-1 text-xs text-theme-text-tertiary dark:text-dark-theme-text-tertiary">
            Get this code from your organization administrator
          </p>
        </div>

        <div>
          <div className="flex items-center space-x-2 mb-1">
            <label htmlFor="password" className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary">
              Password
            </label>
            <PasswordTooltip password={password} email={email} />
          </div>
          <div className="relative">
            <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required value={password} onChange={(e) => {
              setPassword(e.target.value);
              setPasswordErrors([]); // Clear backend errors when user changes password
            }}
              className="appearance-none block w-full px-3 py-2 border border-theme-border dark:border-dark-theme-border rounded-md shadow-sm placeholder-theme-text-tertiary dark:placeholder-dark-theme-text-tertiary focus:outline-none focus:ring-primary-light dark:focus:ring-primary-dark focus:border-primary-light dark:focus:border-primary-dark sm:text-sm bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text pr-10"

              placeholder="Create your password"

            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text focus:outline-none"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
            </button>
          </div>
          <PasswordStrengthIndicator password={password} email={email} showDetails={false} />
          {password && (
            <div className="mt-3">
              <PasswordRequirements 
                password={password} 
                email={email} 
                showTitle={true} 
                className="text-sm"
              />
            </div>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary mb-1">Confirm Password</label>
          <div className="relative">
            <input id="confirmPassword" name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              className="appearance-none block w-full px-3 py-2 border border-theme-border dark:border-dark-theme-border rounded-md shadow-sm placeholder-theme-text-tertiary dark:placeholder-dark-theme-text-tertiary focus:outline-none focus:ring-primary-light dark:focus:ring-primary-dark focus:border-primary-light dark:focus:border-primary-dark sm:text-sm bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text pr-10"

              placeholder="Confirm your password"

            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text focus:outline-none"
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {error && (
          <div className="space-y-2">
            <p className="text-xs text-red-500 dark:text-red-400 text-center">{error}</p>
            {passwordErrors.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                <h4 className="text-xs font-medium text-red-800 dark:text-red-300 mb-2">
                  Backend Validation Errors:
                </h4>
                <ul className="space-y-1">
                  {passwordErrors.map((passwordError, index) => (
                    <li key={index} className="text-xs text-red-700 dark:text-red-300">
                      • {passwordError}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="pt-2">
          <button
            type="submit"
            disabled={loading || !isPasswordValid(password) || password !== confirmPassword || !email || !firstName || !lastName /* || !group */}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-button hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-150"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
          {!loading && (!isPasswordValid(password) || password !== confirmPassword) && password && (
            <p className="mt-2 text-xs text-theme-text-tertiary dark:text-dark-theme-text-tertiary text-center">
              {!isPasswordValid(password) && 'Password must meet security requirements'}
              {isPasswordValid(password) && password !== confirmPassword && 'Passwords must match'}
            </p>
          )}
        </div>
      </form>

      <div className="mt-6 text-center">
        <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
          Already have an account?{' '}
          <button 
            onClick={onSwitchToLogin} 
            className="font-medium text-primary-light dark:text-primary-dark hover:underline focus:outline-none"
          >
            Login
          </button>
        </p>
      </div>
    </div>
  );
};

export default RegisterForm;
